// Wave 1 safety (docs/product/spec-safety.md): the web's pure side. An uploaded picture's `meta.safety` (does it need
// the real-person consent box, or can't it be a reference at all), Admin > Safety's queue (normalised from
// GET /api/admin/safety, sorted open first and critical on top) and the count badge on the admin rail item.
// No DOM, no React: unit-tested in safety.test.ts.

export type SafetyKind = "upload_blocked" | "prompt_blocked" | "minor_signal" | "csam_lock" | "screen_error" | "consent" | "insisted";
export type SafetySeverity = "info" | "warn" | "critical";
export type SafetyStatus = "open" | "dismissed" | "actioned" | "ncmec_review" | "reported";
/** what an admin can set (POST /api/admin/safety/:id/resolve) */
export type SafetyResolve = "dismissed" | "actioned" | "reported";

export type SafetyEvent = {
  id: string;
  userId: string | null;
  userEmail: string | null;
  /** whether the user is locked right now (null: the api didn't say) */
  userLocked: boolean | null;
  kind: SafetyKind;
  severity: SafetySeverity;
  source: string | null;
  categories: string[];
  /** at most 300 chars of the text; never anything for images */
  excerpt: string | null;
  assetId: string | null;
  projectId: string | null;
  status: SafetyStatus;
  note: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
};
export type SafetyConsent = { id: string; userId: string | null; userEmail: string | null; assetId: string | null; fileName: string; statement: string | null; createdAt: string };
export type SafetyCounts = { open: number; critical: number; ncmec: number };
export type SafetyView = { events: SafetyEvent[]; counts: SafetyCounts; consents: SafetyConsent[] };
export type SafetyCount = { open: number; critical: number };

export const KINDS: SafetyKind[] = ["csam_lock", "upload_blocked", "prompt_blocked", "minor_signal", "insisted", "screen_error", "consent"];
export const KIND_LABEL: Record<SafetyKind, string> = {
  upload_blocked: "Upload blocked",
  prompt_blocked: "Prompt blocked",
  minor_signal: "Possible minor",
  csam_lock: "Child safety lock",
  screen_error: "Check didn't run",
  consent: "Consent",
  insisted: "Insisted after a block",
};
export const STATUS_LABEL: Record<SafetyStatus, string> = { open: "Open", dismissed: "Dismissed", actioned: "Actioned", ncmec_review: "NCMEC review", reported: "Reported to NCMEC" };
export const RESOLVE_LABEL: Record<SafetyResolve, string> = { dismissed: "Dismissed", actioned: "Marked actioned", reported: "Marked reported to NCMEC" };

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const strOrNull = (v: unknown) => (typeof v === "string" && v ? v : typeof v === "number" ? String(v) : null);
const count = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : typeof v === "string" && /^\d+$/.test(v) ? +v : 0);
const pick = (o: Record<string, unknown>, ...keys: string[]) => {
  for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};
const oneOf = <T extends string>(v: unknown, all: readonly T[], d: T): T => (all.includes(v as T) ? (v as T) : d);
const STATUSES: SafetyStatus[] = ["open", "dismissed", "actioned", "ncmec_review", "reported"];
const SEVERITIES: SafetySeverity[] = ["info", "warn", "critical"];

// ------------------------------------------------------------------ an asset's meta.safety

export type AssetSafety = { realPerson: boolean; refBlocked: boolean; needsConsent: boolean; consentAt: string | null };
/** What the upload row and the library sheet show for a picture: nothing, the consent box, or "can't be a reference". */
export type SafetyNoticeKind = "ref-blocked" | "needs-consent" | null;

export function assetSafety(meta: Record<string, unknown> | null | undefined): AssetSafety | null {
  const s = obj(obj(meta).safety);
  if (!Object.keys(s).length) return null;
  return { realPerson: s.realPerson === true, refBlocked: s.refBlocked === true, needsConsent: s.needsConsent === true, consentAt: strOrNull(s.consentAt) };
}

export function safetyNotice(meta: Record<string, unknown> | null | undefined): SafetyNoticeKind {
  const s = assetSafety(meta);
  if (!s) return null;
  if (s.refBlocked) return "ref-blocked"; // can't be consented away
  if (s.needsConsent && !s.consentAt) return "needs-consent";
  return null;
}

export const SAFETY_COPY = {
  needsConsent: "This looks like a real person. You can use it as a reference only if it's you or you have their permission.",
  consentBox: "It's me, or I have this person's permission to use their likeness",
  refBlocked: "This picture can't be used as a reference (it looks like it shows a child or a well-known person).",
  consented: "Thanks. You can use it as a reference now.",
  locked: "Your account is paused while we review something. If you think this is a mistake, write to safety@cao.studio.",
  lockedEmail: "safety@cao.studio",
} as const;

// ------------------------------------------------------------------ Admin > Safety

export function normSafetyEvent(raw: unknown): SafetyEvent | null {
  const o = obj(raw);
  const id = strOrNull(o.id);
  if (!id) return null;
  const u = obj(o.user);
  const locked = pick(o, "userLocked", "user_locked", "locked") ?? pick(u, "safetyLocked", "locked");
  const cats = pick(o, "categories");
  return {
    id,
    userId: strOrNull(pick(o, "userId", "user_id") ?? u.id),
    userEmail: strOrNull(pick(o, "userEmail", "user_email", "email") ?? u.email),
    userLocked: typeof locked === "boolean" ? locked : null,
    kind: oneOf(o.kind, KINDS, "screen_error"),
    severity: oneOf(o.severity, SEVERITIES, "info"),
    source: strOrNull(o.source),
    categories: Array.isArray(cats) ? cats.filter((c): c is string => typeof c === "string" && !!c) : [],
    excerpt: strOrNull(o.excerpt),
    assetId: strOrNull(pick(o, "assetId", "asset_id")),
    projectId: strOrNull(pick(o, "projectId", "project_id")),
    status: oneOf(o.status, STATUSES, "open"),
    note: strOrNull(o.note),
    resolvedBy: strOrNull(pick(o, "resolvedBy", "resolved_by")),
    resolvedAt: strOrNull(pick(o, "resolvedAt", "resolved_at")),
    createdAt: strOrNull(pick(o, "createdAt", "created_at")) ?? "",
  };
}

export function normSafetyConsent(raw: unknown): SafetyConsent | null {
  const o = obj(raw);
  const id = strOrNull(o.id);
  if (!id) return null;
  const u = obj(o.user);
  return {
    id,
    userId: strOrNull(pick(o, "userId", "user_id") ?? u.id),
    userEmail: strOrNull(pick(o, "userEmail", "user_email", "email") ?? u.email),
    assetId: strOrNull(pick(o, "assetId", "asset_id")),
    fileName: strOrNull(pick(o, "fileName", "file_name")) ?? "(no name)",
    statement: strOrNull(o.statement),
    createdAt: strOrNull(pick(o, "createdAt", "created_at")) ?? "",
  };
}

export function normSafetyCount(raw: unknown): SafetyCount {
  const o = obj(raw);
  const c = Object.keys(obj(o.counts)).length ? obj(o.counts) : o;
  return { open: count(c.open), critical: count(c.critical) };
}

export function normSafetyView(raw: unknown): SafetyView {
  const o = obj(raw);
  const events = (Array.isArray(o.events) ? o.events : []).map(normSafetyEvent).filter((e): e is SafetyEvent => !!e);
  const consents = (Array.isArray(o.consents) ? o.consents : []).map(normSafetyConsent).filter((e): e is SafetyConsent => !!e);
  const c = obj(o.counts);
  return { events: sortSafety(events), counts: { open: count(c.open), critical: count(c.critical), ncmec: count(c.ncmec) }, consents: [...consents].sort((a, b) => b.createdAt.localeCompare(a.createdAt)) };
}

/** Still needs someone: open, or waiting on the NCMEC review. */
export const isOpen = (e: Pick<SafetyEvent, "status">) => e.status === "open" || e.status === "ncmec_review";
const SEV_RANK: Record<SafetySeverity, number> = { critical: 0, warn: 1, info: 2 };

/** The queue's order: open first, critical on top within each, newest first. */
export function sortSafety<T extends Pick<SafetyEvent, "status" | "severity" | "createdAt">>(events: T[]): T[] {
  return [...events].sort((a, b) => Number(!isOpen(a)) - Number(!isOpen(b)) || SEV_RANK[a.severity] - SEV_RANK[b.severity] || b.createdAt.localeCompare(a.createdAt));
}

/** Filters on the screen (the api filters too; this keeps the list right after a resolve without a reload). */
export function filterSafety(events: SafetyEvent[], f: { status: "open" | "all"; kind: "" | SafetyKind }): SafetyEvent[] {
  return events.filter((e) => (f.status === "all" || isOpen(e)) && (!f.kind || e.kind === f.kind));
}

/** Whether a row offers Unlock: a child-safety lock whose user is (or may still be) locked. */
export const canUnlock = (e: SafetyEvent) => e.kind === "csam_lock" && !!e.userId && e.userLocked !== false;

/** The rail badge: nothing at zero, "99+" past 99, the warning tone when anything critical is open. */
export function safetyBadge(c: SafetyCount | null): { text: string; tone: "critical" | "open"; label: string } | null {
  if (!c || c.open <= 0) return null;
  const crit = Math.min(c.critical, c.open);
  return {
    text: c.open > 99 ? "99+" : String(c.open),
    tone: crit > 0 ? "critical" : "open",
    label: `${c.open} open${crit > 0 ? `, ${crit} critical` : ""}`,
  };
}

/** ["sexual", "public_figure"] -> "sexual · public figure" */
export const categoriesText = (cats: string[]) => cats.map((c) => c.replace(/_/g, " ")).join(" · ");
